import { useCallback } from 'react';
import { useFileManagementContext } from '../../context/FileManagementContext';

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds

/**
 * 🎯 檔案上傳 Hook
 * 專注於檔案上傳和處理邏輯
 */
export const useFileUpload = () => {
  const { actions: fileActions } = useFileManagementContext();

  // 輔助函數：格式化時間長度
  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // 輔助函數：取得音訊時長
  const getAudioDuration = (input) => {
    return new Promise((resolve, reject) => {
      // 檢查輸入是否為 File 物件
      if (input instanceof File) {
        // 檢查檔案類型
        if (!input.type.startsWith("audio/")) {
          reject(new Error("不是有效的音訊檔案"));
          return;
        }

        // 🎯 優化：對所有格式都嘗試 AudioContext 方法
        // 特別適用於 WAV, WebM, OGG 等格式
        if (
          input.type === "audio/wav" ||
          input.type === "audio/wave" ||
          input.type === "audio/x-wav" ||
          input.type === "audio/webm" ||
          input.type === "audio/ogg"
        ) {
          const reader = new FileReader();
          reader.onload = function (e) {
            // 使用 AudioContext 來解析檔案
            const audioContext = new (window.AudioContext ||
              window.webkitAudioContext)();
            audioContext
              .decodeAudioData(e.target.result)
              .then((buffer) => {
                resolve(buffer.duration);
                audioContext.close();
              })
              .catch((err) => {
                console.warn(`AudioContext 解析失敗，嘗試 HTML Audio: ${err.message}`);
                audioContext.close();
                
                // 🔄 後備方案：使用 HTML Audio 元素
                tryHTMLAudio(input, resolve, reject);
              });
          };
          reader.onerror = () => {
            console.warn('檔案讀取失敗，嘗試 HTML Audio');
            tryHTMLAudio(input, resolve, reject);
          };
          reader.readAsArrayBuffer(input);
          return;
        }

        // 非特殊格式使用原本的處理方式
        input = URL.createObjectURL(input);
      }

      // 直接使用 HTML Audio 處理
      tryHTMLAudio(input, resolve, reject);
    });
  };

  // 🎯 輔助函數：HTML Audio 處理
  const tryHTMLAudio = (input, resolve, reject) => {
    const audio = new Audio(input);

    const cleanup = () => {
      if (typeof input === 'string' && input.startsWith('blob:')) {
        URL.revokeObjectURL(input);
      }
    };

    audio.addEventListener("loadedmetadata", () => {
      cleanup();
      if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
        resolve(audio.duration);
      } else {
        reject(new Error("無法取得有效的音訊時長"));
      }
    });

    audio.addEventListener("error", (e) => {
      cleanup();
      reject(new Error(`音訊檔案載入失敗: ${e.message || '未知錯誤'}`));
    });

    // 🎯 增加超時處理
    setTimeout(() => {
      if (audio.readyState === 0) {
        cleanup();
        reject(new Error("音訊檔案載入超時"));
      }
    }, 10000); // 10 秒超時
  };

  // 🎯 新增：取得影片時長
  const getVideoDuration = (input) => {
    return new Promise((resolve, reject) => {
      // 檢查輸入是否為 File 物件
      if (input instanceof File) {
        // 檢查檔案類型
        if (!input.type.startsWith("video/")) {
          reject(new Error("不是有效的影片檔案"));
          return;
        }
        input = URL.createObjectURL(input);
      }

      const video = document.createElement('video');
      video.preload = 'metadata';

      video.addEventListener("loadedmetadata", () => {
        if (input instanceof Blob) {
          URL.revokeObjectURL(input);
        }
        resolve(video.duration);
      });

      video.addEventListener("error", () => {
        if (input instanceof Blob) {
          URL.revokeObjectURL(input);
        }
        reject(new Error("影片檔案載入失敗"));
      });

      video.src = input;
    });
  };

  // 🎯 統一的媒體時長處理函數
  const getMediaDuration = async (file, fileType) => {
    try {
      if (fileType === "audio") {
        return await getAudioDuration(file);
      } else if (fileType === "video") {
        return await getVideoDuration(file);
      }
      return 0;
    } catch (error) {
      console.warn(`${fileType} 時長計算失敗:`, error.message);
      return 0; // 失敗時返回 0，避免阻斷流程
    }
  };

  // 輔助函數：取得圖片尺寸
  const getImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
        });
      };
      img.onerror = () => {
        reject(new Error("圖片載入失敗"));
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // 模擬檔案上傳過程
  const simulateFileUpload = async (fileId) => {
    const totalSteps = 5;
    const stepDelay = 500;

    for (let step = 1; step <= totalSteps; step++) {
      await new Promise(resolve => setTimeout(resolve, stepDelay));

      fileActions.updateFileStatus(fileId, {
        uploadProgress: (step / totalSteps) * 100,
        processingStatus: `上傳中 ${step}/${totalSteps}`
      });
    }
  };

  const handleFileUpload = useCallback(async ({
    file,
    fileSource = 'local',
    formField = [],
  }) => {
    try {
      // console.log('[useFileUpload] handleFileUpload 參數:', {
      //   file,
      //   fileSource,
      //   formField,
      // });

      // 根據檔案 MIME type 判斷檔案類型
      let fileType;
      switch (true) {
        case file.type?.startsWith("audio/"):
        case file.content_type?.startsWith("audio/"):
          fileType = "audio";
          break;
        case file.type?.startsWith("video/"):
        case file.content_type?.startsWith("video/"):
          fileType = "video";
          break;
        case file.type?.startsWith("image/"):
        case file.content_type?.startsWith("image/"):
          fileType = "photo";
          break;
        default:
          // throw new Error("不支援的檔案類型!");
          console.warn('不支援的檔案類型!');
          return;
      }

      // 建立檔案 URL
      const fileUrl = fileSource !== 'local' ? file.url : URL.createObjectURL(file);

      // 🆕 解析 formField 來建立 uploadContext
      const uploadContext = formField.length > 0 ? {
        section: formField[1] || 'unknown', // ceremony.public -> 'public'
        category: formField[3] || 'unknown', // files.my -> 'my'  
        fileType: formField[4] || fileType, // audio/photo/video
        formFieldPath: formField.join('.'), // 完整路徑字串
        timestamp: Date.now() // 上傳時間戳，用於排序
      } : {};

      // 建立基本檔案物件
      const baseFile = {
        uid: file.uid || file.id,
        id: fileSource !== 'local' ? file.id : undefined,
        name: file.name,
        url: fileUrl,
        content_type: fileSource !== 'local' ? file.content_type : file.type,
        size: file.size,
        fileSource,
        status: fileSource !== 'local' ? (file.status || 'done') : 'uploading',
        uploadProgress: fileSource !== 'local' ? 100 : 0,
        // 🆕 添加檔案位置追蹤資訊
        formField,
        uploadContext
      };

      let newFile = baseFile;


      const isAudio = fileType === "audio";
      const isVideo = fileType === "video";
      const isImage = fileType === "photo";
      
      // 🎯 處理音訊和影片檔案的時長
      if (isAudio || isVideo) {
        let duration = file.duration;
        
        // 如果沒有預設 duration 且是本地檔案，則計算時長
        if (!duration && fileSource === 'local') {
          duration = await getMediaDuration(file, fileType);
        }
        
        newFile = {
          ...baseFile,
          duration: fileSource !== 'local' ? file.duration : duration,
          durationFormatted: fileSource !== 'local' ? formatDuration(file.duration || 0) : formatDuration(duration)
        };
      }

      // 處理圖片檔案
      if (isImage) {
        if (fileSource !== 'local') {
          newFile = {
            ...baseFile,
            preview: fileUrl,
            originalWidth: file.originalWidth,
            originalHeight: file.originalHeight,
          };
        } else {
          try {
            const dimensions = await getImageDimensions(file);
            newFile = {
              ...baseFile,
              preview: fileUrl,
              originalWidth: dimensions.width,
              originalHeight: dimensions.height,
            };
          } catch (error) {
            console.warn('無法獲取圖片尺寸:', error);
            newFile = { ...baseFile, preview: fileUrl };
          }
        }
      } else if (isVideo) {
        // 影片處理已在上面的 duration 處理中完成
        if (!newFile.duration) {
          newFile = { ...baseFile, preview: fileUrl };
        } else {
          newFile = { ...newFile, preview: fileUrl };
        }
      }

      // 生成檔案 ID
      const fileId = newFile.id || `${formField.join('-')}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      newFile = { ...newFile, id: fileId, uid: newFile.uid || fileId };

      // 添加到 FileManagementContext
      fileActions.addFile(newFile);

      // 如果是本地上傳的檔案，開始上傳流程
      if (fileSource === 'local') {
        try {
          // await simulateFileUpload(fileId);

          // 更新檔案狀態為完成
          fileActions.updateFileStatus(fileId, {
            status: 'done',
            uploadProgress: 100
          });
        } catch (error) {
          // 更新檔案狀態為錯誤
          fileActions.updateFileStatus(fileId, {
            status: 'error',
            errorMessage: error.message || '檔案上傳失敗'
          });
          throw error;
        }
      }

      // console.log('[useFileUpload] handleFileUpload newFile:', newFile);
      return { file: newFile };
    } catch (error) {
      // 記錄錯誤但不顯示消息，讓調用者決定如何處理錯誤
      console.error('檔案上傳錯誤:', error);
      throw error;
    }
  }, [fileActions]);

  return {
    handleFileUpload,
    getAudioDuration,
    getVideoDuration,
    getMediaDuration,
    getImageDimensions,
  };
}; 